/**
 * Plans v2 card 2.4 (owner, 16.10.2026): a signed-in app keeps working
 * offline for the signed plan claim's 14 days. Past that, while the server
 * cannot be reached, the app REQUIRES sign-in: the desktop gate shows only
 * the sign-in page (platform/desktopGate.js). At a cold start, and when the
 * claim runs out while the app is open. Nothing is deleted or changed; the
 * data is simply not shown until the server confirms the account again.
 *
 *   'ok'       nothing to do (signed out, no claim yet, or time left)
 *   'checking' the claim ran out and the server has not answered yet
 *   'expired'  the claim ran out and the server cannot be reached
 *
 * Within the last 48 hours the claim is renewed while the server is
 * reachable (a long-running app never runs out while online). If it is not
 * reachable, one notice (useNotificationStore, never a toast) warns that
 * sign-in will be required soon.
 *
 * "Reachable" = `GET /auth/user` answers. A server without a signing key (no
 * claim, 503) still answers, so it never locks anyone out.
 */
import i18n from '../i18n';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { authService } from '../services/apiService';
import { classifyAuthInitFailure, clearsSession } from '../store/authInitFailure';
import { refreshEntitlements } from './entitlementsFeed';
import { lastPlanClaim } from './planClaim';
import { isTauri } from '../utils/isTauri';
import { useClaimExpiryStore } from './claimExpiryState';

export { useClaimExpiryStore };

export const WARN_BEFORE_MS = 48 * 60 * 60 * 1000;
export const CHECK_EVERY_MS = 60 * 1000;

let warnedFor = null;
let warningId = null;
let inFlight = null;

/**
 * Rust stops the MCP server while sign-in is required, and lets it start
 * again (per the account's settings) once it is not (`mcp_set_sign_in_required`).
 * Nothing serves while the app shows the sign-in page, native terminal tools
 * included. Desktop only; best effort (a failure leaves the bus refusal).
 */
async function tellRust(required) {
  if (!isTauri()) return;
  try {
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('mcp_set_sign_in_required', { required });
  } catch {
    // the JS bus still refuses while sign-in is required
  }
}

const setState = (state) => {
  const was = useClaimExpiryStore.getState().state;
  if (was === state) return;
  useClaimExpiryStore.setState({ state });
  if (state === 'expired') tellRust(true);
  else if (was === 'expired') tellRust(false);
};

function dropWarning() {
  if (warningId != null) useNotificationStore.getState().removeNotification(warningId);
  warningId = null;
}

function warnOnce(key, msLeft) {
  if (warnedFor === key) return;
  warnedFor = key;
  warningId = useNotificationStore.getState().addNotification({
    type: 'warning',
    title: i18n.t('plans.claimExpiry.warnTitle'),
    message: i18n.t('plans.claimExpiry.warnMessage', { count: Math.max(1, Math.ceil(msLeft / 3_600_000)) }),
    duration: 0,
  });
}

/** Does the server answer for this session? A 401 ends the session elsewhere (the gate then shows sign-in anyway). */
async function reachable(getUser) {
  try {
    await getUser();
    return true;
  } catch (error) {
    return clearsSession(classifyAuthInitFailure(error));
  }
}

/**
 * One check. `deps` are for tests. Resolves with the new state.
 */
export function checkClaimExpiry(now = Date.now(), deps = {}) {
  if (inFlight) return inFlight;
  const {
    getUser = authService.getUser,
    renew = (user) => refreshEntitlements(user, authService.getUsage, authService.getPlanClaim),
  } = deps;
  const { isAuthenticated, isOffline, user } = useAuthStore.getState();
  const last = lastPlanClaim();
  if (!isAuthenticated || user?.id == null || !last || last.userId !== String(user.id)) {
    dropWarning();
    setState('ok');
    return Promise.resolve('ok');
  }
  const msLeft = last.exp * 1000 - now;
  if (msLeft > WARN_BEFORE_MS) {
    dropWarning();
    setState('ok');
    return Promise.resolve('ok');
  }
  // A cold start that already found the server unreachable needs no probe.
  if (msLeft <= 0 && isOffline) {
    dropWarning();
    setState('expired');
    return Promise.resolve('expired');
  }
  if (msLeft <= 0 && useClaimExpiryStore.getState().state === 'ok') setState('checking');
  inFlight = (async () => {
    const ok = await reachable(getUser);
    if (ok) {
      // Renew the claim while we can (best effort; a server without a
      // signing key keeps answering, so the app stays open).
      await renew(user);
      dropWarning();
      setState('ok');
      return 'ok';
    }
    if (msLeft > 0) {
      warnOnce(`${last.userId}:${last.exp}`, msLeft);
      setState('ok');
      return 'ok';
    }
    dropWarning();
    setState('expired');
    return 'expired';
  })().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** Tests only. */
export function resetClaimExpiryForTests() {
  warnedFor = null;
  warningId = null;
  inFlight = null;
  useClaimExpiryStore.setState({ state: 'ok' });
}
